/**
 * What the current user may do on the staged-products surface.
 *
 * Resolved server-side by the same middleware that enforces the gates, so the
 * buttons and the API can never disagree — the alternative is re-deriving
 * grants in the browser from role plumbing it only half sees, which drifts.
 *
 * Keep the scope names in sync with `src/types/contentScopes.ts`.
 */

import { useQuery } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';

export type ContentScope =
  | 'content_review'
  | 'content_approve'
  | 'content_promote'
  | 'auto_promote_admin';

export const CONTENT_SCOPE_LABELS: Record<ContentScope, string> = {
  content_review: 'Review content',
  content_approve: 'Approve & reject',
  content_promote: 'Promote to catalog',
  auto_promote_admin: 'Auto-promotion policy',
};

export const CONTENT_SCOPE_DESCRIPTIONS: Record<ContentScope, string> = {
  content_review: 'View and edit staged products, run AI enhancement and verification',
  content_approve: 'Approve, reject, and un-reject staged products',
  content_promote: 'Promote to the live catalog and run bulk pipeline waves',
  auto_promote_admin: 'Change which brands promote automatically, and the live switch',
};

export const CONTENT_SCOPE_ORDER: ContentScope[] = [
  'content_review',
  'content_approve',
  'content_promote',
  'auto_promote_admin',
];

export interface ContentScopesResult {
  scopes: ContentScope[];
  isPlatformAdmin: boolean;
  /** True once the answer is known; gates stay closed until then. */
  loaded: boolean;
  can: (scope: ContentScope) => boolean;
}

export function useContentScopes(): ContentScopesResult {
  const query = useQuery({
    queryKey: ['staged-content-scopes'],
    // Grants change rarely; re-checking on every mount is noise.
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/staged-products/my-scopes', { headers });
      if (!res.ok) throw new Error('Failed to load permissions');
      const json = await res.json();
      return json.data as { scopes: ContentScope[]; isPlatformAdmin: boolean };
    },
  });

  const scopes = query.data?.scopes || [];
  return {
    scopes,
    isPlatformAdmin: query.data?.isPlatformAdmin === true,
    loaded: query.isSuccess,
    // Fail closed while loading or on error: an action briefly hidden is a
    // smaller problem than one offered and then rejected.
    can: (scope: ContentScope) => query.isSuccess && scopes.includes(scope),
  };
}
